import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import { Clock, Users, Activity, Mic, TrendingUp } from 'lucide-react';
import type { TutorAnalyticsSummary } from '../../../tutor/types/tutorAnalytics';

interface TutorKpiCardsProps {
  data: TutorAnalyticsSummary;
}

export const TutorKpiCards: React.FC<TutorKpiCardsProps> = ({ data }) => {
  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const formatLastActive = (dateString: string | null) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
    return `${Math.floor(diffInDays / 30)} months ago`;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
      {/* Total Minutes */}
      <Card className="hover:shadow-lg transition-shadow">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-app-text-secondary dark:text-gray-400 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Total Minutes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-app-text dark:text-white">
            {data.total_tutor_minutes > 0 ? formatDuration(data.total_tutor_minutes) : '—'}
          </div>
        </CardContent>
      </Card>

      {/* Sessions Count */}
      <Card className="hover:shadow-lg transition-shadow">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-app-text-secondary dark:text-gray-400 flex items-center gap-2">
            <Users className="w-4 h-4" />
            Sessions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-app-text dark:text-white">
            {data.sessions_count || '—'}
          </div>
        </CardContent>
      </Card>

      {/* Last Active */}
      <Card className="hover:shadow-lg transition-shadow">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-app-text-secondary dark:text-gray-400 flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Last Active
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-lg font-semibold text-app-text dark:text-white">
            {formatLastActive(data.last_active_at)}
          </div>
        </CardContent>
      </Card>

      {/* Speaking Level */}
      <Card className="hover:shadow-lg transition-shadow">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-app-text-secondary dark:text-gray-400 flex items-center gap-2">
            <Mic className="w-4 h-4" />
            Speaking Level
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-app-text dark:text-white">
            {data.latest_speaking_level || '—'}
          </div>
        </CardContent>
      </Card>

      {/* Overall Score */}
      <Card className="hover:shadow-lg transition-shadow">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-app-text-secondary dark:text-gray-400 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Overall Score
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-app-text dark:text-white">
            {data.latest_overall_score !== null ? `${data.latest_overall_score}%` : '—'}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};